import { useRolodexFileLS, useRolodexSignedURLs } from 'features/rolodex'
import { ComponentId } from 'pages/reporting/reports/rug/types'
import { useCallback, useMemo } from 'react'

export interface AssetScreenshots {
  fullComponentHeatmap?: {
    componentId: ComponentId
    imagePath: string
    signedUrl?: string
  }[]
  sectionHeatmap?: {
    componentId: ComponentId
    sectionIdx: number
    imagePath: string
    signedUrl?: string
  }[]
  fullComponentDrawing?: {
    componentId: ComponentId
    imagePath: string
    signedUrl?: string
    extension: string
  }[]
}

export const useAssetScreenshots = (metadataEntryId: string) => {
  const { fileRefs: screenshotRefs, forceRefresh: forceRefreshScreenshotRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-screenshot/**',
    })

  const { fileRefs: drawingRefs, forceRefresh: forceRefreshDrawingRefs } =
    useRolodexFileLS({
      entry_id: metadataEntryId,
      path: 'asset-drawing/**',
    })

  const allFileRefs = useMemo(
    () => [...screenshotRefs, ...drawingRefs],
    [screenshotRefs, drawingRefs]
  )

  const {
    results: assetDrawingUrls,
    forceRefresh: forceRefreshAssetDrawingUrls,
  } = useRolodexSignedURLs(
    allFileRefs.map((ref) => ({
      entry_id: metadataEntryId,
      path: ref.path,
    }))
  )

  const assetDrawings = useMemo(() => {
    const drawings: AssetScreenshots = {
      fullComponentHeatmap: [],
      sectionHeatmap: [],
      fullComponentDrawing: [],
    }

    assetDrawingUrls.forEach((result) => {
      if (result.path?.includes('fullComponentHeatmap')) {
        // Extract component ID from path: asset-screenshot/fullComponentHeatmap/<componentId>.png
        const match = result.path.match(/fullComponentHeatmap\/(.+)\.png$/)
        if (match) {
          const componentId = match[1] as ComponentId
          drawings.fullComponentHeatmap?.push({
            componentId,
            imagePath: result.path,
            signedUrl: result.signed_url,
          })
        }
      } else if (result.path?.includes('sectionHeatmap')) {
        // Extract component ID and section index from path: asset-screenshot/sectionHeatmap/<componentId>/<sectionIdx>.png
        const match = result.path.match(/sectionHeatmap\/(.+)\/(\d+)\.png$/)
        if (match) {
          const componentId = match[1] as ComponentId
          const sectionIdx = parseInt(match[2], 10)
          drawings.sectionHeatmap?.push({
            componentId,
            sectionIdx,
            imagePath: result.path,
            signedUrl: result.signed_url,
          })
        }
      } else if (result.path?.includes('fullComponentDrawing')) {
        // Extract component ID and extension from path: asset-drawing/fullComponentDrawing/<componentId>.<extension>
        const match = result.path.match(/fullComponentDrawing\/(.+)\.(\w+)$/)
        if (match) {
          const componentId = match[1] as ComponentId
          const extension = match[2]
          drawings.fullComponentDrawing?.push({
            componentId,
            imagePath: result.path,
            signedUrl: result.signed_url,
            extension,
          })
        }
      }
    })
    return Object.keys(drawings).length > 0 ? drawings : undefined
  }, [assetDrawingUrls])

  const forceRefresh = useCallback(async () => {
    await forceRefreshScreenshotRefs()
    await forceRefreshDrawingRefs()
    await forceRefreshAssetDrawingUrls()
  }, [
    forceRefreshScreenshotRefs,
    forceRefreshDrawingRefs,
    forceRefreshAssetDrawingUrls,
  ])

  return {
    assetScreenshots: assetDrawings,
    forceRefresh,
  }
}
